import {
  CloseSmallIcon,
  HamburgerIcon,
  HomeIcon,
  LogoOperations,
} from '@/components/icons'
import { useAuthContext } from '@/features/auth/context'
import { useTranslation } from '@/hooks/useTranslation'
import { Box, Button, IconButton } from '@mui/material'
import { Stack } from '@mui/system'
import MenuAccordion, { MenuAccordionItem } from './MenuAccordion'
import {
  getMenuDataConfig,
  getMenuMain,
  getMenuOther,
  getMenuSensors,
  getMenuStationConfig,
} from './models'
import { useMenuContext } from './MenuContext'
import SelectLabelDashboard from '@/features/operations/components/pages/Dashboard/SelectLabelDashboard'

import { useEffect, useRef } from 'react'

interface MenuCollapseProps {
  toggleMenu: () => void
}

const MenuCollapse = ({ toggleMenu }: MenuCollapseProps) => {
  const { t } = useTranslation()
  const { interfaceId } = useAuthContext()

  const {
    isMenuExpanded,
    setIsMenuExpanded,
    isDisabledExpanded,
    setIsDisabledExpanded,
  } = useMenuContext()

  const sidebarRef = useRef<HTMLDivElement>(null)
  const mousePos = useRef({ x: 0, y: 0 })

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      mousePos.current = { x: e.clientX, y: e.clientY }
    }
    window.addEventListener('mousemove', handleMouseMove)
    return () => window.removeEventListener('mousemove', handleMouseMove)
  }, [])

  // When isDisabledExpanded goes from true → false, check if mouse is still inside
  const prevDisabled = useRef(isDisabledExpanded)
  useEffect(() => {
    if (prevDisabled.current && !isDisabledExpanded) {
      // Dropdown just closed — check if mouse is outside sidebar
      if (sidebarRef.current) {
        const rect = sidebarRef.current.getBoundingClientRect()
        const { x, y } = mousePos.current
        if (
          x < rect.left ||
          x > rect.right ||
          y < rect.top ||
          y > rect.bottom
        ) {
          setIsMenuExpanded(false)
        }
      }
    }
    prevDisabled.current = isDisabledExpanded
  }, [isDisabledExpanded, setIsMenuExpanded])

  const handleMouseEnter = () => {
    setIsMenuExpanded(true)
  }
  const handleMouseLeave = () => {
    setIsMenuExpanded(false)
  }

  const handleToggle = () => {
    setIsMenuExpanded(!isMenuExpanded)
  }

  return (
    <Box
      ref={sidebarRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      sx={{
        width: isMenuExpanded ? '258px' : '76px',
        height: '100vh',
        bgcolor: '#002031',
        position: 'fixed',
        left: 0,
        top: 0,
        zIndex: 1200,
        transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        overflowX: 'hidden',
        overflowY: 'auto',
        '&::-webkit-scrollbar': {
          display: 'none',
        },
        scrollbarWidth: 'none',
        '&::after': {
          content: '""',
          position: 'absolute',
          right: '-10px',
          top: 0,
          bottom: 0,
          width: '10px',
        },
      }}
    >
      <Box
        sx={{
          color: '#8A8A8A',
          width: '258px',
        }}
      >
        {isMenuExpanded ? (
          <Box
            className="drawer__header"
            sx={{
              padding: '20px',
              borderBottom: '1px solid rgba(255,255,255,0.3)',
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              gap="15px"
              mb="15px"
            >
              <LogoOperations
                fill="white"
                sx={{ width: '94px', height: '17px' }}
              />
            </Stack>
            <Stack>
              <SelectLabelDashboard
                sx={{
                  width: '218px',
                  '& .select-label__dashboard, & .select-label__wrap__item': {
                    width: '218px',
                  },
                  '& .select-label__dashboard': {
                    padding: '9px 12px',
                  },
                  '& .select-label__dashboard p': {
                    fontSize: '12px',
                    fontWeight: 400,
                  },
                }}
                colorIcon="#66f0a0"
                width="218px"
                onOpen={() => setIsDisabledExpanded(true)}
                onClose={() => setIsDisabledExpanded(false)}
              />
            </Stack>
          </Box>
        ) : (
          <Button
            sx={{
              borderRadius: '5px',
              background: 'rgba(255, 255, 255, 0.20)',
              boxShadow: '0px 0px 30px 0px rgba(132, 132, 132, 0.25)',
              padding: '8px',
              '&:hover': {
                background: 'rgba(255, 255, 255, 0.20)',
                color: 'common.white',
                boxShadow: '0px 0px 30px 0px rgba(132, 132, 132, 0.25)',
              },
              height: '32px',
              minWidth: '32px',
              width: '32px',
              color: 'common.white',
              margin: '20px',
            }}
            onClick={handleToggle}
          >
            <HamburgerIcon width={32} />
          </Button>
        )}

        <Box sx={{ padding: isMenuExpanded ? '20px' : '0' }}>
          {isMenuExpanded ? (
            <>
              <MenuAccordionItem
                icon={<HomeIcon />}
                text={t('menuDashboard')}
                to={'/dashboard'}
                sx={{ marginBottom: '20px' }}
                closeMenu={() => {}}
              />
              <MenuAccordion
                id="left-menu-main-menu"
                title={t('menuOperationsTitle')}
                alwaysExpanded
                sx={{
                  '& .MuiAccordionSummary-root': { paddingTop: 0 },
                }}
              >
                {getMenuMain(t, Number(interfaceId)).map((i) => (
                  <MenuAccordionItem
                    disabled={i.disabled}
                    key={i.label}
                    icon={i.icon}
                    text={i.label}
                    to={i.link || ''}
                    closeMenu={() => {}}
                  />
                ))}
              </MenuAccordion>
              <MenuAccordion
                id="left-menu-sensors"
                title={t('menuSensorsTitle')}
                alwaysExpanded
              >
                {getMenuSensors(t, Number(interfaceId)).map((i) => (
                  <MenuAccordionItem
                    disabled={i.disabled}
                    key={i.label}
                    icon={i.icon}
                    text={i.label}
                    to={i.link || ''}
                    closeMenu={() => {}}
                  />
                ))}
              </MenuAccordion>
              <MenuAccordion
                id="left-menu-station-config"
                title={t('menuStationConfigTitle')}
                alwaysExpanded
              >
                {getMenuStationConfig(t).map((i) => (
                  <MenuAccordionItem
                    disabled={i.disabled}
                    key={i.label}
                    icon={i.icon}
                    text={i.label}
                    to={i.link || ''}
                    closeMenu={() => {}}
                  />
                ))}
              </MenuAccordion>
              <MenuAccordion
                id="left-menu-data-config"
                title={t('menuDataConfigTitle')}
                alwaysExpanded
              >
                {getMenuDataConfig(t).map((i) => (
                  <MenuAccordionItem
                    disabled={i.disabled}
                    key={i.label}
                    icon={i.icon}
                    text={i.label}
                    to={i.link || ''}
                    closeMenu={() => {}}
                  />
                ))}
              </MenuAccordion>
              <MenuAccordion
                id="left-menu-other"
                title={t('menuOtherTitle')}
                alwaysExpanded
              >
                {getMenuOther(t).map((i) => (
                  <MenuAccordionItem
                    disabled={i.disabled}
                    key={i.label}
                    icon={i.icon}
                    text={i.label}
                    to={i.link || ''}
                    closeMenu={() => {}}
                  />
                ))}
              </MenuAccordion>
            </>
          ) : (
            <>
              <Box
                sx={{
                  borderBottom: '1px solid rgba(255,255,255,0.3)',
                }}
              >
                <MenuAccordionItem
                  icon={<HomeIcon />}
                  text={t('menuDashboard')}
                  to={'/dashboard'}
                  closeMenu={() => {}}
                  menuCollapse
                />
              </Box>
              <Box
                sx={{
                  borderBottom: '1px solid rgba(255,255,255,0.3)',
                }}
              >
                {getMenuMain(t, Number(interfaceId)).map((i) => (
                  <MenuAccordionItem
                    disabled={i.disabled}
                    key={i.label}
                    icon={i.icon}
                    text={i.label}
                    to={i.link || ''}
                    closeMenu={() => {}}
                    menuCollapse
                  />
                ))}
              </Box>
              <Box
                sx={{
                  borderBottom: '1px solid rgba(255,255,255,0.3)',
                }}
              >
                {getMenuSensors(t, Number(interfaceId)).map((i) => (
                  <MenuAccordionItem
                    disabled={i.disabled}
                    key={i.label}
                    icon={i.icon}
                    text={i.label}
                    to={i.link || ''}
                    closeMenu={() => {}}
                    menuCollapse
                  />
                ))}
              </Box>
              <Box
                sx={{
                  borderBottom: '1px solid rgba(255,255,255,0.3)',
                }}
              >
                {getMenuStationConfig(t).map((i) => (
                  <MenuAccordionItem
                    disabled={i.disabled}
                    key={i.label}
                    icon={i.icon}
                    text={i.label}
                    to={i.link || ''}
                    closeMenu={() => {}}
                    menuCollapse
                  />
                ))}
              </Box>
              <Box
                sx={{
                  borderBottom: '1px solid rgba(255,255,255,0.3)',
                }}
              >
                {getMenuDataConfig(t).map((i) => (
                  <MenuAccordionItem
                    disabled={i.disabled}
                    key={i.label}
                    icon={i.icon}
                    text={i.label}
                    to={i.link || ''}
                    closeMenu={() => {}}
                    menuCollapse
                  />
                ))}
              </Box>
              <Box
                sx={{
                  borderBottom: '1px solid rgba(255,255,255,0.3)',
                }}
              >
                {getMenuOther(t).map((i) => (
                  <MenuAccordionItem
                    disabled={i.disabled}
                    key={i.label}
                    icon={i.icon}
                    text={i.label}
                    to={i.link || ''}
                    closeMenu={() => {}}
                    menuCollapse
                  />
                ))}
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Box>
  )
}

export default MenuCollapse
